import {
  Button,
  ErrorMessage,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
  TextInput,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { GiftCard } from '@vend/types'
import React from 'react'
import { OmniGiftCardSettings } from '../../../controllers/gift-cards/balances-gift-cards'
import { containsGiftCardByNumber } from '../../../functions/sale'
import { useAppSelector } from '../../../state/hooks'
import { selectLineItems } from '../../../state/slices/currentSale/currentSaleSlice'
import {
  useGiftCardProviderSettings,
  useGiftCardSearchStrings,
  useRetrieveGiftCard,
} from '../hooks'
import { getFeatureFromCache } from '../../../util/feature-cache'
import {
  GiftCardModalFlow,
  GiftCardModalState,
  GiftCardType,
} from './GiftCardModal'

const getTitle = (flow: GiftCardModalFlow): React.JSX.Element | '' => {
  return (
    (flow === 'SELLING' && (
      <T
        _str="Add new or manage existing gift card"
        _context="Selling gift card modal header"
      />
    )) ||
    (flow === 'REDEEMING' && (
      <T
        _str="Scan or Enter a Gift Card Number"
        _context="Redeeming gift card modal header"
      />
    )) ||
    ''
  )
}

const getDescription = (flow: GiftCardModalFlow): React.JSX.Element | '' => {
  return (
    (flow === 'SELLING' && (
      <T
        _str="Add a new gift card to the sale, check the balance of an existing gift card, or reload an existing gift card."
        _context="Selling gift card modal description"
      />
    )) ||
    (flow === 'REDEEMING' && (
      <T
        _str="Check an existing card’s status and balance"
        _context="Redeeming gift card modal description"
      />
    )) ||
    ''
  )
}

const getGiftCardCodeRegex = (giftCardType: GiftCardType): RegExp => {
  switch (giftCardType) {
    case 'BALANCES':
      return /^[-0-9a-zA-Z]{1,64}$/
    case 'BIGCOMMERCE':
      return /^[-0-9a-zA-Z]{1,15}$/
  }
}

interface Props {
  flow: GiftCardModalFlow
  giftCardSettings: OmniGiftCardSettings | null
  giftCardType: GiftCardType
  setGiftCard: React.Dispatch<React.SetStateAction<GiftCard | null>>
  setState: React.Dispatch<React.SetStateAction<GiftCardModalState>>
  onClose: () => void
  onError: () => void
}

export const GiftCardSearch: React.FC<Props> = ({
  flow,
  giftCardSettings,
  giftCardType,
  setGiftCard,
  setState,
  onClose,
  onError,
}) => {
  const t = useT()

  const lineItems = useAppSelector(selectLineItems)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isInvalidCode, setIsInvalidCode] = React.useState(false)
  const [isTooShortCode, setIsTooShortCode] = React.useState(false)
  const [giftCardCodeInput, setGiftCardCodeInput] = React.useState('')
  const {
    giftCardMinChars,
    giftCardMaxChars,
    giftCardCodeCapitalised,
  } = useGiftCardProviderSettings(flow, giftCardType)

  const {
    placeholder,
    invalidCharactersErrorMessage,
    tooShortErrorMessage,
    findButtonText,
  } = useGiftCardSearchStrings(giftCardType, giftCardMinChars)

  const retrieveGiftCard = useRetrieveGiftCard(
    flow,
    giftCardType,
    giftCardSettings,
    onClose,
    onError
  )

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    if (containsGiftCardByNumber(lineItems, giftCardCodeInput)) {
      ToastNotifications.negative(
        t(
          'This gift card has already been added to the sale. Use a different gift card number.',
          {
            _context: 'Gift card already added to sale error message',
          }
        )
      )
      setIsSubmitting(false)
      return
    }

    const giftCard = await retrieveGiftCard(giftCardCodeInput)
    if (!giftCard) {
      setIsSubmitting(false)
      return
    }

    setGiftCard(giftCard)

    if (giftCard?.status === 'INACTIVE') {
      // Selling new gift card
      setState('SET_VALUE')
      return
    }

    setState('VIEW_DETAILS')
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setGiftCardCodeInput(value)

    const isInvalid =
      value.length > 0 && !getGiftCardCodeRegex(giftCardType).test(value)
    setIsInvalidCode(isInvalid)

    const isTooShort = value.length > 0 && value.length < giftCardMinChars
    setIsTooShortCode(isTooShort)
  }

  const isSearchDisabled = getFeatureFromCache('omni_gift_cards')
    ? isInvalidCode ||
      isTooShortCode ||
      giftCardCodeInput.length === 0 ||
      giftCardSettings === null
    : isInvalidCode || isTooShortCode || giftCardCodeInput.length === 0

  return (
    <form onSubmit={e => handleSubmit(e)}>
      <ModalHeader>{getTitle(flow)}</ModalHeader>

      <ModalContent>
        <Text>{getDescription(flow)}</Text>
        <div className="vd-mt5">
          <Text type="label">{giftCardCodeCapitalised}</Text>
        </div>
        <TextInput
          className="vd-mt1"
          maxLength={giftCardMaxChars}
          autoFocus
          required
          placeholder={placeholder}
          hasError={isInvalidCode || isTooShortCode}
          onChange={e => handleCodeChange(e)}
          disabled={isSubmitting}
        ></TextInput>
        {isInvalidCode ? (
          <ErrorMessage>{invalidCharactersErrorMessage}</ErrorMessage>
        ) : isTooShortCode ? (
          <ErrorMessage>{tooShortErrorMessage}</ErrorMessage>
        ) : null}
      </ModalContent>

      <ModalActions>
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={isSearchDisabled}
          data-track="find-gift-card"
        >
          {findButtonText}
        </Button>
      </ModalActions>
    </form>
  )
}
